import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import './styles/global.css';
import './styles/icons.css';

function handleResetCacheParam() {
  try {
    const url = new URL(window.location.href);
    const resetRequested = url.searchParams.has('reset-cache');
    const resetKey = 'ghost:reset-cache-applied';

    if (!resetRequested) {
      sessionStorage.removeItem(resetKey);
      return;
    }

    // One-time guard prevents accidental reload loops if navigation is interrupted.
    if (sessionStorage.getItem(resetKey) === '1') {
      url.searchParams.delete('reset-cache');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      sessionStorage.removeItem(resetKey);
      return;
    }

    sessionStorage.setItem(resetKey, '1');
    url.searchParams.delete('reset-cache');
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  } catch (err) {
    // Ignore URL/sessionStorage issues in restricted browser contexts.
  }
}

handleResetCacheParam();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);


// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();

// Mobile viewport/keyboard helpers
let layoutViewportHeight = window.innerHeight;
let keyboardOpen = false;
let touchStartY = 0;
let keyboardStabilizeTimers = [];

function clearKeyboardStabilizeTimers() {
  keyboardStabilizeTimers.forEach(timer => clearTimeout(timer));
  keyboardStabilizeTimers = [];
}

function scheduleKeyboardOpenStabilize() {
  clearKeyboardStabilizeTimers();
  [120, 280].forEach(delay => {
    const timer = setTimeout(() => {
      requestAnimationFrame(() => updateVhAndToolbar());
    }, delay);
    keyboardStabilizeTimers.push(timer);
  });
}

function isScrollableElement(node) {
  if (!(node instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(node);
  const overflowY = style.overflowY;
  const canScrollY = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return canScrollY && node.scrollHeight > node.clientHeight + 1;
}

function canScrollFromTarget(target, deltaY) {
  let current = target instanceof HTMLElement ? target : null;
  while (current && current !== document.body && current !== document.documentElement) {
    if (isScrollableElement(current)) {
      const scrollTop = current.scrollTop;
      const maxScrollTop = current.scrollHeight - current.clientHeight;
      if (deltaY > 0 && scrollTop < maxScrollTop) return true;
      if (deltaY < 0 && scrollTop > 0) return true;
    }
    current = current.parentElement;
  }
  return false;
}

function onTouchStart(event) {
  if (!keyboardOpen || !event.touches || event.touches.length === 0) return;
  touchStartY = event.touches[0].clientY;
}

function onTouchMove(event) {
  if (!keyboardOpen || !event.touches || event.touches.length === 0) return;
  const currentY = event.touches[0].clientY;
  const deltaY = touchStartY - currentY;
  if (deltaY === 0) return;

  if (!canScrollFromTarget(event.target, deltaY)) {
    event.preventDefault();
  }
}

function onWindowScroll() {
  if (!keyboardOpen) return;
  if (window.scrollX !== 0 || window.scrollY !== 0) {
    window.scrollTo(0, 0);
  }
}

function updateVhAndToolbar() {
  const wasKeyboardOpen = keyboardOpen;
  const visualViewport = window.visualViewport;
  const visibleHeight = visualViewport ? visualViewport.height : window.innerHeight;

  // keyboardInset: positive when keyboard is visible and covering part of the layout
  const viewportTopOffset = visualViewport ? visualViewport.offsetTop : 0;
  const keyboardInset = Math.max(0, window.innerHeight - (visibleHeight + viewportTopOffset));

  // Keep app/page height tied to layout viewport (full height), not visual viewport.
  // Only refresh this when keyboard is closed so keyboard open does not shrink the app.
  if (keyboardInset <= 0) {
    layoutViewportHeight = window.innerHeight;
  }
  document.documentElement.style.setProperty('--vh', `${layoutViewportHeight * 0.01}px`);

  const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const baseBottom = isTouch ? 0 : 40; // match CSS defaults
  // give a small gap (8px) above the keyboard
  const bottom = keyboardInset > 0 ? keyboardInset + 8 : baseBottom;
  // expose as CSS variable so transitions and responsive styles can use it
  document.documentElement.style.setProperty('--toolbar-bottom', `${bottom}px`);
  // add keyboard-open class so CSS consumers can hide or adjust widgets above keyboard
  keyboardOpen = keyboardInset > 0;

  if (keyboardOpen) {
    document.body.classList.add('keyboard-open');
    document.documentElement.classList.add('keyboard-open');
  } else {
    document.body.classList.remove('keyboard-open');
    document.documentElement.classList.remove('keyboard-open');
  }

  if (!wasKeyboardOpen && keyboardOpen) {
    scheduleKeyboardOpenStabilize();
  } else if (!keyboardOpen) {
    clearKeyboardStabilizeTimers();
  }
}

// Retry helper: run update multiple times shortly after keyboard shows to avoid race conditions
function scheduleVhUpdates() {
  updateVhAndToolbar();
  [60, 180, 400].forEach(delay => setTimeout(updateVhAndToolbar, delay));
}

// Initialize and keep in sync with visual viewport changes
scheduleVhUpdates();
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', scheduleVhUpdates);
  window.visualViewport.addEventListener('scroll', scheduleVhUpdates);
}
window.addEventListener('resize', scheduleVhUpdates);
// also react to focus changes (inputs/contenteditable) which often precede keyboard open
window.addEventListener('focusin', scheduleVhUpdates);
window.addEventListener('focusout', scheduleVhUpdates);
document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
window.addEventListener('scroll', onWindowScroll, { passive: true });

// Try to prevent Chrome password manager overlay on contenteditable by marking body and
// adding an off-screen dummy login form (keeps inputs in DOM but out of view).
try {
  document.documentElement.setAttribute('autocomplete', 'off');
  document.body.setAttribute('autocomplete', 'off');
  const dummyId = 'ghost-autofill-blocker';
  if (!document.getElementById(dummyId)) {
    const form = document.createElement('form');
    form.id = dummyId;
    form.autocomplete = 'off';
    form.style.position = 'absolute';
    form.style.left = '-9999px';
    form.style.top = '-9999px';
    form.style.width = '1px';
    form.style.height = '1px';
    form.style.overflow = 'hidden';

    const u = document.createElement('input');
    u.type = 'text';
    u.name = 'username';
    u.autocomplete = 'username';
    u.tabIndex = -1;

    form.appendChild(u);
    // Prepend the hidden form so password heuristics see it before the editor DOM
    if (document.body.firstChild) document.body.insertBefore(form, document.body.firstChild);
    else document.body.appendChild(form);
  }
} catch (err) {
  // Ignore DOM errors in non-browser environments
}
